import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Inbox, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  no_route: "bg-amber-50 text-amber-800 border-amber-200",
  error: "bg-red-50 text-red-800 border-red-200",
  empty: "bg-stone-50 text-stone-500 border-stone-200",
};

export default function AdminDiagnostics() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/diagnostics/whatsapp?limit=100");
      setData(d);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  const t = (ar, en) => (lang === "ar" ? ar : en);
  const s = data?.summary || {};
  const statusLabel = {
    ok: t("ناجحة", "OK"),
    no_route: t("بدون مسار", "No route"),
    error: t("خطأ", "Error"),
    empty: t("حدث حالة", "Status event"),
  };

  return (
    <div className="space-y-6" data-testid="admin-diagnostics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900">{t("تشخيص اتصال واتساب", "WhatsApp Connection Diagnostics")}</h1>
          <p className="text-sm text-stone-500 mt-1">{t("آخر أحداث Meta الواردة وحالة توجيهها إلى Chatwoot — تحديث تلقائي كل 15 ثانية", "Latest Meta webhook events and their Chatwoot routing status — auto-refreshes every 15s")}</p>
        </div>
        <Button data-testid="diagnostics-refresh-btn" onClick={load} variant="outline" className="rounded-xl" disabled={loading}>
          {loading ? <Loader2 size={16} className="animate-spin me-2" /> : <RefreshCw size={16} className="me-2" />}
          {t("تحديث", "Refresh")}
        </Button>
      </div>

      {err && <div data-testid="diagnostics-error" className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-800">{String(err)}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "ok", icon: CheckCircle2, color: "text-emerald-600" },
          { k: "no_route", icon: AlertTriangle, color: "text-amber-600" },
          { k: "error", icon: XCircle, color: "text-red-600" },
          { k: "empty", icon: Inbox, color: "text-stone-400" },
        ].map(({ k, icon: Icon, color }) => (
          <Card key={k} data-testid={`diag-stat-${k}`} className="rounded-2xl border-stone-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Icon size={22} className={color} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{s[k] ?? 0}</p>
                <p className="text-xs text-stone-500">{statusLabel[k]}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl border-stone-200">
        <CardContent className="p-5">
          <h2 className="font-heading font-bold text-stone-900 mb-3">{t("المسارات النشطة (الأرقام المربوطة)", "Active routes (connected numbers)")}</h2>
          {(data?.routes || []).length === 0 ? (
            <p data-testid="diag-no-routes" className="text-sm text-stone-500">{t("لا توجد أرقام مربوطة بعد — الرسائل الواردة ستُسقط بحالة no_route", "No numbers routed yet — incoming messages will be dropped as no_route")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="diag-routes-table" className="w-full text-sm">
                <thead>
                  <tr className="text-start text-xs text-stone-500 border-b border-stone-100">
                    <th className="py-2 text-start">{t("الرقم", "Phone")}</th>
                    <th className="py-2 text-start">Phone Number ID</th>
                    <th className="py-2 text-start">{t("العميل", "Client")}</th>
                    <th className="py-2 text-start">{t("صندوق Chatwoot", "Chatwoot inbox")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.routes.map((r) => (
                    <tr key={r.phone_number_id} className="border-b border-stone-50">
                      <td className="py-2 font-medium text-stone-800" dir="ltr">{r.display_phone_number || "—"}</td>
                      <td className="py-2 text-stone-500 font-mono text-xs" dir="ltr">{r.phone_number_id}</td>
                      <td className="py-2 text-stone-700">{r.user_email}</td>
                      <td className="py-2 text-stone-500">#{r.chatwoot_inbox_id} ({t("حساب", "acct")} {r.chatwoot_account_id})</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-stone-200">
        <CardContent className="p-5">
          <h2 className="font-heading font-bold text-stone-900 mb-3">{t("آخر الأحداث الواردة", "Recent incoming events")} <span className="text-xs font-normal text-stone-400">({data?.total_events ?? 0})</span></h2>
          {(data?.events || []).length === 0 ? (
            <p data-testid="diag-no-events" className="text-sm text-stone-500">{t("لم تصل أي أحداث من Meta بعد. تأكد من إعداد Webhook في Meta Console.", "No Meta events received yet. Verify the webhook config in Meta Console.")}</p>
          ) : (
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table data-testid="diag-events-table" className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-xs text-stone-500 border-b border-stone-100">
                    <th className="py-2 text-start">{t("الوقت", "Time")}</th>
                    <th className="py-2 text-start">{t("الحالة", "Status")}</th>
                    <th className="py-2 text-start">{t("المرسل", "Sender")}</th>
                    <th className="py-2 text-start">Phone Number ID</th>
                    <th className="py-2 text-start">{t("التفاصيل", "Details")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((e, i) => (
                    <tr key={i} className="border-b border-stone-50">
                      <td className="py-2 text-stone-500 text-xs whitespace-nowrap" dir="ltr">{(e.received_at || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="py-2">
                        <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[e.status] || STATUS_STYLES.empty}`}>{statusLabel[e.status] || e.status}</Badge>
                      </td>
                      <td className="py-2 text-stone-700 font-mono text-xs" dir="ltr">{e.wa_id || "—"}</td>
                      <td className="py-2 text-stone-500 font-mono text-xs" dir="ltr">{e.phone_number_id || "—"}</td>
                      <td className="py-2 text-stone-600 text-xs max-w-[320px] truncate" title={e.detail}>{e.detail || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
